import { useCallback } from 'react';
import { AppliedModifierFormData } from '../constant/applied_modifiers_constants';
import * as AppliedModifiersConstants from '../constant/applied_modifiers_constants';

/**
 * Custom hook for managing validation logic
 */
export const useAppliedModifiersValidation = (formData: AppliedModifierFormData, isSubmitted: boolean) => {
  const validateField = useCallback((fieldName: string) => {
    // Simple validation - return valid for now
    return { isValid: true };
  }, []);

  const shouldShowValidation = useCallback((fieldName: string) => {
    if (!isSubmitted) return false;
    
    // Get the list of required fields based on current form state
    const baseRequiredFields = [
      AppliedModifiersConstants.field_identifier.setting_name,
      AppliedModifiersConstants.field_identifier.modifier,
      AppliedModifiersConstants.field_identifier.to_charge_by,
      AppliedModifiersConstants.field_identifier.date_of_service_range,
      AppliedModifiersConstants.field_identifier.tabs_wrapper
    ];

    // Add conditional required fields
    const isProviderTypeSelected = formData.provider_type && 
                                 formData.provider_type !== AppliedModifiersConstants.ProviderTypeValues.NONE;
    const isPrimarySpecialtySelected = formData.primary_specialty && 
                                      Array.isArray(formData.primary_specialty) && 
                                      formData.primary_specialty.length > 0;

    const requiredFields = [...baseRequiredFields];
    
    if (isProviderTypeSelected) {
      requiredFields.push(AppliedModifiersConstants.field_identifier.primary_specialty);
    }
    
    if (isPrimarySpecialtySelected) {
      requiredFields.push(AppliedModifiersConstants.field_identifier.provider_type);
    }

    // Only show validation for fields that are actually required
    if (requiredFields.indexOf(fieldName) === -1) {
      return false;
    }

    // Map field identifier to actual form data property name
    const getFormDataPropertyName = (fieldIdentifier: string): keyof AppliedModifierFormData => {
      switch (fieldIdentifier) {
        case AppliedModifiersConstants.field_identifier.setting_name:
          return 'setting_name';
        case AppliedModifiersConstants.field_identifier.active_status:
          return 'active_status';
        case AppliedModifiersConstants.field_identifier.modifier:
          return 'modifier';
        case AppliedModifiersConstants.field_identifier.to_charge_by:
          return 'to_charge_by';
        case AppliedModifiersConstants.field_identifier.date_of_service_range:
          return 'start_date'; // This is a special case - we'll handle it separately
        case AppliedModifiersConstants.field_identifier.primary_specialty:
          return 'primary_specialty';
        case AppliedModifiersConstants.field_identifier.provider_type:
          return 'provider_type';
        case AppliedModifiersConstants.field_identifier.tabs_wrapper:
          return 'payer'; // This is a special case - we'll handle it separately
        default:
          return fieldName as keyof AppliedModifierFormData;
      }
    };

    // Handle special cases for date range and tabs wrapper
    let fieldValue: any;
    if (fieldName === AppliedModifiersConstants.field_identifier.date_of_service_range) {
      // For date range, check both start_date and end_date
      fieldValue = formData.start_date && formData.end_date ? `${formData.start_date} - ${formData.end_date}` : undefined;
    } else if (fieldName === AppliedModifiersConstants.field_identifier.tabs_wrapper) {
      // For tabs wrapper, check if any of the tab data has selections
      fieldValue = (formData.payer && formData.payer.length > 0) || 
                   (formData.insurance_class && formData.insurance_class.length > 0) || 
                   (formData.excluded_insurance_class && formData.excluded_insurance_class.length > 0) ? 
                   'has_selections' : undefined;
    } else {
      // For regular fields, get the actual form data property
      const propertyName = getFormDataPropertyName(fieldName);
      fieldValue = formData[propertyName];
    }
    
    
    if (Array.isArray(fieldValue)) {
      return fieldValue.length === 0;
    }
    
    // Handle different field types
    if (typeof fieldValue === 'string') {
      return !fieldValue || fieldValue.trim() === '' || fieldValue === '0';
    }
    
    if (typeof fieldValue === 'boolean') {
      return fieldValue === undefined || fieldValue === null;
    }
    
    return !fieldValue || fieldValue === '' || fieldValue === '0';
  }, [formData, isSubmitted]);

  const markFieldForRequiredValidation = useCallback((fieldName: string) => {
    return shouldShowValidation(fieldName);
  }, [shouldShowValidation]);

  const markFieldForValidation = useCallback((fieldName: string) => {
    return shouldShowValidation(fieldName);
  }, [shouldShowValidation]);

  const isFormValid = useCallback(() => {
    const baseRequiredFields = [
      AppliedModifiersConstants.field_identifier.setting_name,
      AppliedModifiersConstants.field_identifier.modifier,
      AppliedModifiersConstants.field_identifier.to_charge_by,
      AppliedModifiersConstants.field_identifier.date_of_service_range,
      AppliedModifiersConstants.field_identifier.tabs_wrapper
    ];

    // Add conditional required fields
    const isProviderTypeSelected = formData.provider_type && 
                                 formData.provider_type !== AppliedModifiersConstants.ProviderTypeValues.NONE;
    const isPrimarySpecialtySelected = formData.primary_specialty && 
                                      Array.isArray(formData.primary_specialty) && 
                                      formData.primary_specialty.length > 0;

    const requiredFields = [...baseRequiredFields];
    
    if (isProviderTypeSelected) {
      requiredFields.push(AppliedModifiersConstants.field_identifier.primary_specialty);
    }
    
    if (isPrimarySpecialtySelected) {
      requiredFields.push(AppliedModifiersConstants.field_identifier.provider_type);
    }

    // Validate all required fields
    for (const fieldName of requiredFields) {
      const result = validateField(fieldName);
      if (!result.isValid) {
        return false;
      }
    }

    return true;
  }, [formData, validateField]);

  return {
    validateField,
    shouldShowValidation,
    markFieldForRequiredValidation,
    markFieldForValidation,
    isFormValid
  };
};
